import type { CSSProperties, MouseEvent } from 'react';
import {
  ArrowRight,
  ArrowUpRight,
  Instagram,
  Link2,
  Play,
  Youtube,
} from 'lucide-react';
import { safeExternalHref } from '../../utils/safeExternalHref';
import { safeCssBackgroundImageUrl } from '../../utils/safeCssBackgroundImageUrl';
import { youtubeThumbnailUrl } from '../../utils/parseYoutubeVideoId';
import type { ProfileComponentViewModel } from './profileViewModel';

const PLATFORM_COLORS: Record<string, string> = {
  instagram: '#C9447A',
  youtube: '#C0392B',
  x: '#1A1A1A',
  twitter: '#1A1A1A',
  linkedin: '#2B5E8C',
};

interface ProfileComponentBlockProps {
  comp: ProfileComponentViewModel;
  accent: string;
  onClick?: (comp: ProfileComponentViewModel) => void;
}

export function ProfileComponentBlock({ comp, accent, onClick }: ProfileComponentBlockProps) {
  const stop = (e: MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick(comp);
    }
  };

  if (comp.type === 'social') {
    const color = PLATFORM_COLORS[comp.platform ?? ''] ?? accent;
    const href = safeExternalHref(comp.url);
    const content = (
      <>
        <span className="pp-row-ic" style={{ '--ic': color } as CSSProperties}>
          <SocialIcon platform={comp.platform} />
        </span>
        <span className="pp-row-main">
          <span className="pp-row-label">{comp.label}</span>
          {comp.handle ? <span className="pp-row-sub">{comp.handle}</span> : null}
        </span>
        <span className="pp-row-arr">
          <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
        </span>
      </>
    );
    if (href) {
      return (
        <a href={href} className="pp-row pp-row--social" onClick={stop}>
          {content}
        </a>
      );
    }
    return <div className="pp-row pp-row--social">{content}</div>;
  }

  if (comp.type === 'link') {
    const href = safeExternalHref(comp.url);
    const content = (
      <>
        <span className="pp-row-main">
          <span className="pp-row-label">{comp.label}</span>
          {comp.note ? <span className="pp-row-sub">{comp.note}</span> : null}
        </span>
        <span className="pp-row-arr">
          <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </span>
      </>
    );
    if (href) {
      return (
        <a href={href} className="pp-row pp-row--link" onClick={stop}>
          {content}
        </a>
      );
    }
    return <div className="pp-row pp-row--link">{content}</div>;
  }

  if (comp.type === 'youtube') {
    const href = safeExternalHref(comp.url);
    const thumb =
      comp.videoId && comp.videoId !== 'x' ? youtubeThumbnailUrl(comp.videoId) : undefined;
    const frame = (
      <>
        <span
          className="pp-yt-frame"
          style={thumb ? ({ backgroundImage: `url(${thumb})` } as CSSProperties) : undefined}
        >
          <span className="pp-yt-play">
            <Play className="h-5 w-5 fill-current" strokeWidth={2} />
          </span>
          <span className="pp-yt-tag">YouTube</span>
        </span>
        <span className="pp-card-cap">{comp.label}</span>
      </>
    );
    if (href) {
      return (
        <a href={href} className="pp-card pp-card--yt" onClick={stop}>
          {frame}
        </a>
      );
    }
    return <div className="pp-card pp-card--yt">{frame}</div>;
  }

  if (comp.type === 'instagram') {
    const href = safeExternalHref(comp.url);
    const frame = (
      <>
        <span className="pp-ig-frame">
          <span className="pp-ig-badge">
            <Instagram className="h-4 w-4" strokeWidth={2} />
          </span>
        </span>
        <span className="pp-card-cap">{comp.caption || comp.label}</span>
      </>
    );
    if (href) {
      return (
        <a href={href} className="pp-card pp-card--ig" onClick={stop}>
          {frame}
        </a>
      );
    }
    return <div className="pp-card pp-card--ig">{frame}</div>;
  }

  if (comp.type === 'text') {
    return (
      <div className="pp-text">
        <p className="pp-text-label">{comp.label}</p>
        <p className="pp-text-body">{comp.body}</p>
      </div>
    );
  }

  if (comp.type === 'image') {
    const safeImage = safeCssBackgroundImageUrl(comp.imageUrl);
    const href = safeExternalHref(comp.url);
    const frame = (
      <>
        <span
          className="pp-ig-frame"
          style={safeImage ? ({ backgroundImage: `url(${safeImage})` } as CSSProperties) : undefined}
        />
        {comp.label ? <span className="pp-card-cap">{comp.label}</span> : null}
      </>
    );
    if (href) {
      return (
        <a href={href} className="pp-card" onClick={stop}>
          {frame}
        </a>
      );
    }
    return <div className="pp-card">{frame}</div>;
  }

  return null;
}

function SocialIcon({ platform }: { platform?: string }) {
  if (platform === 'youtube') return <Youtube className="h-5 w-5" strokeWidth={2} />;
  if (platform === 'instagram') return <Instagram className="h-5 w-5" strokeWidth={2} />;
  return <Link2 className="h-5 w-5" strokeWidth={2} />;
}
